import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Truck, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { orderAPI } from '../utils/api';
import { toast } from 'sonner';
import { SEO } from '../seo/SEO';

const statusSteps = [
  { key: 'received', label: 'Order received', icon: Package },
  { key: 'confirmed', label: 'Restaurant confirmed your order', icon: CheckCircle },
  { key: 'preparing', label: 'Your order is being prepared', icon: Package },
  { key: 'ready', label: 'Your order is ready', icon: Truck },
  { key: 'out_for_delivery', label: 'Your order is on the way', icon: Truck },
  { key: 'completed', label: 'Completed', icon: CheckCircle },
];

// Legacy orders created before the "received" state used "pending" as the
// initial status. Treat it as the same customer-facing step.
const legacyStatusMap = { pending: 'received' };

const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await orderAPI.getOrder(orderId);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Order not found');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStepIndex = () => {
    if (!order) return 0;
    const rawStatus = order.order_status;
    const status = legacyStatusMap[rawStatus] || rawStatus;
    const idx = statusSteps.findIndex(step => step.key === status);
    return idx === -1 ? 0 : idx;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Order not found</h1>
          <Button onClick={() => navigate('/')}>Go to Home</Button>
        </div>
      </div>
    );
  }

  const currentStep = getCurrentStepIndex();

  return (
    <div className="min-h-screen bg-background pb-8">
      <SEO title="Order tracking" noIndex includeRestaurantSchema={false} />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-3xl font-bold mb-2">Track Your Order</h1>
          <p className="text-muted-foreground mb-8">Order ID: {order.id}</p>

          <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
            <div className="relative">
              <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gray-200" />
              <div className="space-y-8">
                {statusSteps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = index <= currentStep;
                  const isCurrent = index === currentStep;

                  return (
                    <div key={step.key} className="relative flex items-center gap-4">
                      <div
                        className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 ${
                          isActive
                            ? 'bg-primary border-primary'
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <Icon
                          className={`h-6 w-6 ${
                            isActive ? 'text-white' : 'text-gray-400'
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <p
                          className={`font-semibold ${
                            isActive ? 'text-foreground' : 'text-muted-foreground'
                          }`}
                        >
                          {step.label}
                        </p>
                        {isCurrent && (
                          <p className="text-sm text-primary">Current Status</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-bold mb-4">Order Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Order Type:</span>
                <span className="font-medium capitalize">{order.order_type}</span>
              </div>
              {order.delivery_address && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Delivery Address:</span>
                  <span className="font-medium text-right">{order.delivery_address}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Amount:</span>
                <span className="font-bold text-primary">£{order.total_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Payment Status:</span>
                <span className="font-medium capitalize">{order.payment_status}</span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t">
              <h3 className="font-semibold mb-3">Items</h3>
              <div className="space-y-2">
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span>{item.name} x {item.quantity}</span>
                    <span className="font-medium">£{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderTrackingPage;
